import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, delay, Observable, of, retry, retryWhen, take, throwError, timeout, TimeoutError } from 'rxjs';
import { UserData } from 'src/core/types/user.types';
import { ExactSystem, SystemIBase, SystemSearchResults } from '../types/selectedSystem.types';
import { AutocheckData, EventDetails, MaintenanceDetails, PSMData, SubsystemErrors, SystemHealthLastUpdated, SystemHealthOverview } from '../types/systemhealth.types';
import { EscalationsData, NotiDetails, NotificationsData, NotiSiteResponse, PartsData, PsmSiteResponse, ServiceHistoryOverview, ServiceHistoryOverviewSite, SitePartsResponse, SiteVisitsResponse, TimelineResponse, UpcomingServices, VisitsData } from '../types/servicehistory.types';
import { ServiceMetricsOverview, ServiceMetricsOverviewSite } from '../types/servicemetrics.types';
import { FunctionalLocationOverview } from '../types/funcloc.types';
import { AppConfigPayload } from './appconfig.service';
import { ExactSite, SiteSearchResults } from '../types/selectedSite.types';
import { upcomingserviceData } from '../types/upcomingservicedetails';
import { BookmarkDeviceResponse, BookmarkSiteResponse } from '../types/bookmarks.types';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  redirectUrl: string = '';
  userData: UserData;

  constructor(private http: HttpClient) { }

  // ####################################################################################
  // App Config APIs
  // ####################################################################################

  getFeatureFlags(): Observable<AppConfigPayload> {
    return this.http.get<AppConfigPayload>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/appconfig").pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  // ####################################################################################
  // Device APIs
  // ####################################################################################

  getIBaseInformation(material, serial): Observable<{ response: SystemIBase; status: number }> {
    return this.http.get<{ response: SystemIBase; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getIBaseInformation/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPrompts(material, serial): Observable<{ response: any; status: number }> {
    return this.http.get<{ response: any; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPrompts/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPromptsSite(siteId): Observable<{ response: any; status: number }> {
    return this.http.get<{ response: any; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPromptsSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  searchDevice(searchobject): Observable<{ response: SystemSearchResults; status: number }> {
    return this.http.post<{ response: SystemSearchResults; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/searchDevice", searchobject).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  searchSite(searchobject): Observable<{ response: SiteSearchResults; status: number }> {
    return this.http.post<{ response: SiteSearchResults; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/searchSite", searchobject).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  accessDevice(searchobject): Observable<{ response: boolean; status: number }> {
    return this.http.post<{ response: any; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/accessDevice", searchobject).pipe(
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    )
  }

  accessSite(searchobject): Observable<{ response: boolean; status: number }> {
    return this.http.post<{ response: any; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/accessSite", searchobject).pipe(
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    )
  }

  searchExact(material, serial): Observable<{ response: ExactSystem; status: number }> {
    return this.http.get<{ response: ExactSystem; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/searchExact/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  searchExactSite(siteId): Observable<{ response: ExactSite; status: number }> {
    return this.http.get<{ response: ExactSite; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/searchExactSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  // ####################################################################################
  // Overview APIs
  // ####################################################################################

  getSystemHealthOverview(material, serial): Observable<{ response: SystemHealthOverview; status: number }> {
    return this.http.get<{ response: SystemHealthOverview; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getSystemHealthOverview/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getServiceHistoryOverview(material, serial): Observable<{ response: ServiceHistoryOverview; status: number }> {
    return this.http.get<{ response: ServiceHistoryOverview; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getServiceHistoryOverview/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getServiceMetricsOverview(material, serial): Observable<{ response: ServiceMetricsOverview; status: number }> {
    return this.http.get<{ response: ServiceMetricsOverview; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getServiceMetricsOverview/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getFunctionalLocationOverview(func_loc): Observable<{ response: FunctionalLocationOverview; status: number }> {

    return this.http.get<{ response: FunctionalLocationOverview; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getFunctionalLocationOverview/" + func_loc).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  //Upcoming services APIs
  getUpcomingServicesOverview(material, serial): Observable<{ response: UpcomingServices; status: number }> {
    return this.http.get<{ response: UpcomingServices; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getUpcomingServicesOverview/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)  
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  // ####################################################################################
  // Overview APIs Site
  // ####################################################################################

  getServiceMetricsOverviewSite(func_loc_id): Observable<{ response: ServiceMetricsOverviewSite; status: number }> {
    return this.http.get<{ response: ServiceMetricsOverviewSite; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getServiceMetricsOverviewSite/" + func_loc_id).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getServiceHistoryOverviewSite(func_loc_id): Observable<{ response: ServiceHistoryOverviewSite; status: number }> {
    return this.http.get<{ response: ServiceHistoryOverviewSite; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getServiceHistoryOverviewSite/" + func_loc_id).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getUpcomingServicesSiteOverview(siteId): Observable<{ response: UpcomingServices; status: number }> {
    return this.http.get<{ response: UpcomingServices; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getUpcomingServicesSiteOverview/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)  
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  // ####################################################################################
  // System Health Detail APIs
  // ####################################################################################

  getMaintenance(material, serial): Observable<{ response: MaintenanceDetails; status: number }> {
    return this.http.get<{ response: MaintenanceDetails; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getMaintenance/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getAutocheck(material, serial): Observable<{ response: AutocheckData; status: number }> {
    return this.http.get<{ response: AutocheckData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getAutocheck/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getErrorSummary(material, serial): Observable<{ response: SubsystemErrors; status: number }> {
    return this.http.get<{ response: SubsystemErrors; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getErrorSummary/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getErrors(material, serial, subsystem): Observable<{ response: EventDetails; status: number }> {
    return this.http.get<{ response: EventDetails; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getErrors/" + material + "/" + serial + "/" + subsystem).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }




  // ####################################################################################
  // Service History Detail APIs
  // ####################################################################################

  getNotifications(material, serial): Observable<{ response: NotificationsData; status: number }> {
    return this.http.get<{ response: NotificationsData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getNotifications/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getNotificationDetails(material, serial, notification): Observable<{ response: NotiDetails; status: number }> {
    return this.http.get<{ response: NotiDetails; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getNotificationDetails/" + material + "/" + serial + "/" + notification).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getNotificationSummary(notification): Observable<{ response: TimelineResponse; status: number }> {
    return this.http.get<{ response: TimelineResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getNotificationSummary/" + notification).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPartsUsed(material, serial): Observable<{ response: PartsData; status: number }> {
    return this.http.get<{ response: PartsData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPartsUsed/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getVisits(material, serial): Observable<{ response: VisitsData; status: number }> {
    return this.http.get<{ response: VisitsData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getVisits/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPSMs(material, serial): Observable<{ response: PSMData; status: number }> {
    return this.http.get<{ response: PSMData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPSMs/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPSMDetails(pattern) {
    return this.http.get(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPSMDetails/" + pattern).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getEscalations(material, serial): Observable<{ response: EscalationsData; status: number }> {
    return this.http.get<{ response: EscalationsData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getEscalations/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  // ####################################################################################
  // Service History Details Site
  // ####################################################################################


  getNotificationsSite(siteId): Observable<{ response: NotiSiteResponse; status: number }> {
    return this.http.get<{ response: NotiSiteResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getNotificationsSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  getPartsUsedSite(siteId): Observable<{ response: SitePartsResponse; status: number }> {
    return this.http.get<{ response: SitePartsResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPartsUsedSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getVisitsSite(siteId): Observable<{ response: SiteVisitsResponse; status: number }> {
    return this.http.get<{ response: SiteVisitsResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getVisitsSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getPSMsSite(siteId): Observable<{ response: PsmSiteResponse; status: number }> {
    return this.http.get<{ response: PsmSiteResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getPSMsSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }



  // ####################################################################################
  // Upcoming Service Events Details Site
  // ####################################################################################


  getUpcomingServicesDetails(material, serial): Observable<{ response: upcomingserviceData; status: number }> {
    return this.http.get<{ response; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getUpcomingServicesDetails/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)  
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  getUpcomingServicesSiteDetails(siteId): Observable<{ response: upcomingserviceData; status: number }> {
    return this.http.get<{ response: upcomingserviceData; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getUpcomingServicesSiteDetails/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)  
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }



  // ####################################################################################
  // Bookmark APIs
  // ####################################################################################

  getBookmarkDevice(material, serial): Observable<{ response: BookmarkDeviceResponse; status: number }> {
    return this.http.get<{ response: BookmarkDeviceResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getBookmarkDevice/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 2, delay: 1000 }),
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  getBookmarkSite(siteId): Observable<{ response: BookmarkSiteResponse; status: number }> {
    return this.http.get<{ response: BookmarkSiteResponse; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getBookmarkSite/" + siteId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 2, delay: 1000 }),
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }


  // ####################################################################################
  getSystemHealthLastUpdated(material, serial): Observable<{ response: SystemHealthLastUpdated; status: number }> {
    return this.http.get<{ response: SystemHealthLastUpdated; status: number }>(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getSystemHealthLastUpdated/" + material + "/" + serial).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }



  //Subscription Form Selection APIs
  getAccounts() {
    return this.http.get(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getAccounts/").pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

  getSerialPerAccount(accountId) {
    return this.http.get(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/getSerialPerAccount/" + accountId).pipe(
      // Put timeout either *per attempt* (here) or as an overall cap (see note below)
      timeout(30000),
      retry({ count: 5, delay: 2000 }), // after 5 retries, it will ERROR (and hit your subscriber's error)
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Request timed out after 30s');
        }
        return throwError(() => error);
      })
    );
  }

}